import React, { useEffect, useState } from "react";
import { Form, Image, Upload, Select } from "antd";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { UploadDocumentApi } from "../../../../api/request/kyc.js";
import { setUserDetails } from "../../../../store/Slices/userSlice.js";
import LoadableButton from "../../../../components/buttons/LoadableButton.jsx";
import UploadDoc from "../../../../assets/UploadDoc.jsx";
import Lock from "../../../../assets/Lock.jsx";
import Delete from "../../../../assets/Delete.jsx";
import Dragger from "antd/es/upload/Dragger.js";

const KycForm = () => {
  const dispatch = useDispatch();
  const { userDetails } = useSelector((state) => state.user);
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const [fileFrontList, setFileFrontList] = useState([]);
  const [fileBackList, setFileBackList] = useState([]);
  const [previewImage, setPreviewImage] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showBothSidesMessage, setShowBothSidesMessage] = useState(false);

  const getBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewOpen(true);
  };

  const handleFrontChange = ({ fileList }) => {
    setFileFrontList(fileList);
    form.setFieldsValue({ UploadDocumentFront: fileList });
  };

  const handleBackChange = ({ fileList }) => {
    setFileBackList(fileList);
    form.setFieldsValue({ UploadDocumentBack: fileList });
  };

  const FronSideUploadButton = (
    <div className="flex justify-center flex-col items-center  md:py-4 md:px-5">
      <div className="flex justify-center w-auto mb-5 px-4 py-4 bg-gray-100 rounded-md pointer-events-none opacity-75">
        <UploadDoc color="#7367F0"></UploadDoc>
      </div>
      <div className="text-base font-medium capitalize mb-1 pointer-events-none text-gray-500">
        Upload Front side of document
      </div>
      <div className="text-gray-400">
        Drag and drop your files here or{" "}
        <span className="text-[#7367F0] cursor-pointer">Click to Select</span>
      </div>
      <div className="text-gray-400">Supported Formets: .jpg,.jpeg,.png</div>
    </div>
  );

  const BackSideUploadButton = (
    <div className="flex justify-center flex-col items-center md:py-4 md:px-5">
      <div className="flex justify-center w-auto mb-5 px-4 py-4 bg-gray-100 rounded-md pointer-events-none opacity-75">
        <UploadDoc color="#28C76F"></UploadDoc>
      </div>
      <div className="text-base font-medium capitalize mb-1 pointer-events-none text-gray-500">
        Upload back side of document
      </div>
      <div className="text-gray-400">
        Drag and drop your files here or{" "}
        <span className="text-[#28C76F] cursor-pointer">Click to Select</span>
      </div>
      <div className="text-gray-400">Supported Formets: .jpg,.jpeg,.png</div>
    </div>
  );

  const handleSubmit = async () => {
    const uploadedFront = fileFrontList.map((file) => file.originFileObj);
    const uploadedBack = fileBackList.map((file) => file.originFileObj);
    if (uploadedFront.length !== 1 || uploadedBack.length !== 1) {
      setShowBothSidesMessage(true);
      toast.error("Please upload both sides of the selected document.");
      return;
    }

    const formData = new FormData();
    formData.append("NationalID", true);
    formData.append("UserId", userDetails?._id);
    formData.append("UploadDocument", uploadedFront[0]);
    formData.append("UploadDocument", uploadedBack[0]);
    formData.append("UploadSignature", uploadedBack[0]);

    try {
      setIsLoading(true);
      let apiHeaders = {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data",
        },
      };
      const { data } = await UploadDocumentApi(formData, apiHeaders);
      if (data?.success) {
        dispatch(setUserDetails({ ...userDetails, kycStatus: "Pending" }));
        toast.success(data?.message);
        form.resetFields();
        setFileFrontList([]);
        setFileBackList([]);
        setShowBothSidesMessage(false);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error?.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="kyc-container">
      <div className="container">
        <div className="w-full min-h-[calc(100vh-200px)] flex flex-col items-center justify-center">
          <div className="mb-9">
            <div className="flex items-center justify-center mb-3">
              <div className="h-[40px] w-[40px] bg-[#FFF0E1] flex items-center justify-center me-3 rounded-xl">
                <Lock color="#FF9F43"></Lock>
              </div>
              <p className="font-bold text-[#2F2B3D] sm:text-3xl text-xl text-center">
                Upload KYC Documents
              </p>
            </div>
            <p className="text-gray-500 text-base text-center">
              Please upload clear front and back images of your valid ID for
              verification.
            </p>
          </div>
          <Form form={form} onFinish={handleSubmit}>
            <div className="flex md:flex-nowrap flex-wrap lg:gap-6 gap-3 justify-center">
              <div>
                <Form.Item name="Upload Document" rules={[{ required: true }]}>
                  <Dragger
                    accept=".jpg,.jpeg,.png"
                    showUploadList={false}
                    beforeUpload={() => false}
                    fileList={fileFrontList}
                    onPreview={handlePreview}
                    onChange={handleFrontChange}
                    className="front-upload"
                    disabled={fileFrontList.length > 0}
                  >
                    <div
                      className="w-full h-full"
                      onDragOver={(e) =>
                        fileFrontList.length > 0 && e.preventDefault()
                      }
                      onDrop={(e) =>
                        fileFrontList.length > 0 && e.preventDefault()
                      }
                    >
                      {fileFrontList.length < 1 ? (
                        FronSideUploadButton
                      ) : (
                        <div className="relative flex justify-center items-center">
                          <img
                            src={URL.createObjectURL(
                              fileFrontList[0].originFileObj
                            )}
                            alt="front"
                            className="rounded-lg w-[300px] h-[190px] sm:h-[200px] sm:w-[345px] object-contain"
                          />
                          <button
                            type="button"
                            className="absolute top-0 -right-0 rounded-md bg-[#FFE2E3] border-primary p-1.5 flex items-center justify-center text-sm font-bolder"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFileFrontList([]);
                            }}
                          >
                            <Delete />
                          </button>
                        </div>
                      )}
                    </div>
                  </Dragger>
                </Form.Item>
              </div>

              <div className="flex flex-wrap lg:gap-6 gap-3 justify-center">
                <Form.Item name="Upload Document" rules={[{ required: true }]}>
                  <Dragger
                    accept=".jpg,.jpeg,.png"
                    showUploadList={false}
                    beforeUpload={() => false}
                    fileList={fileBackList}
                    onPreview={handlePreview}
                    onChange={handleBackChange}
                    className="back-upload"
                    disabled={fileBackList.length > 0}
                  >
                    <div
                      className="w-full h-full"
                      onDragOver={(e) =>
                        fileBackList.length > 0 && e.preventDefault()
                      }
                      onDrop={(e) =>
                        fileBackList.length > 0 && e.preventDefault()
                      }
                    >
                      {fileBackList.length < 1 ? (
                        BackSideUploadButton
                      ) : (
                        <div className="relative flex justify-center items-center">
                          <img
                            src={URL.createObjectURL(
                              fileBackList[0].originFileObj
                            )}
                            alt="back"
                            className="rounded-lg w-[300px] h-[190px] sm:h-[200px] sm:w-[345px] object-contain"
                          />
                          <button
                            type="button"
                            className="absolute top-0 -right-0 rounded-md bg-[#FFE2E3] border-primary p-1.5 flex items-center justify-center text-sm font-bolder"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFileBackList([]);
                            }}
                          >
                            <Delete />
                          </button>
                        </div>
                      )}
                    </div>
                  </Dragger>
                </Form.Item>
              </div>
            </div>

            {showBothSidesMessage && (
              <div className="text-red-500 mb-4 mt-6 text-center">
                Please upload both sides of the selected document.
              </div>
            )}
            <div className="flex justify-center mt-5">
              <LoadableButton
                className="bg-primary text-white text-base font-semibold rounded-lg px-12 py-2 themeHover duration-500"
                disabled={isLoading}
                type="Submit"
                lable="Send Request"
                isLoading={isLoading}
                loadingLable="Sending Request..."
              />
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default KycForm;
